import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
      { title: "Forgot password | RoleBoard" },
      {
        name: "description",
        content: "Reset your RoleBoard password by email.",
      },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://roleboard.app/forgot-password" }],
  }),
  component: ForgotPasswordPage,
});

const schema = z.object({
  email: z.string().trim().email("Enter a valid email").max(255),
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse({ email });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setSubmitting(true);
    try {
      // Note: We intentionally don't reveal whether the account exists.
      await supabase.auth.resetPasswordForEmail(parsed.data.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      setSent(true);
    } catch {
      // Still show the same confirmation to avoid account enumeration.
      setSent(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-60"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 0%, color-mix(in oklab, var(--primary) 22%, transparent) 0%, transparent 70%)",
        }}
      />
      <div className="min-h-screen grid place-items-center px-5 py-10">
        <div className="w-full max-w-md">
          <Link to="/" className="flex flex-col items-center gap-3 mb-10" aria-label="RoleBoard home">
            <BrandLogo className="h-20 md:h-24 w-auto" />
          </Link>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-semibold tracking-tight leading-tight">
              Reset your password
            </h1>
            <p className="mt-3 text-[15px] text-muted-foreground max-w-sm mx-auto">
              Enter your email and we'll send you a link to set a new password.
            </p>
          </div>

          <div className="rounded-2xl border border-border bg-card/60 backdrop-blur-xl p-6 shadow-xl shadow-black/20">
            {sent ? (
              <div className="space-y-4 text-center">
                <p className="text-[15px]">Password reset link sent</p>
                <p className="text-sm text-muted-foreground">
                  If an account exists for {email}, you'll receive an email shortly.
                </p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/login">Back to log in</Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full h-10" disabled={submitting}>
                  {submitting ? <Loader2 className="size-4 animate-spin" /> : "Send reset link"}
                </Button>
                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Back to log in
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
